import axios from 'axios';
import config from '../config/config';

// Централізований сервіс для управління статусом видалення
class DeletionStatusService {
  constructor() {
    this.cache = null;
    this.lastCheckTime = 0;
    this.isChecking = false;
    this.CACHE_DURATION = 300000; // 5 хвилин для продакшну
    this.subscribers = new Set();
    this.pollInterval = null;
    this.POLL_INTERVAL = 120000; // Перевіряємо кожні 2 хвилини замість 10 секунд
  }

  // Підписка на зміни статусу
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  // Сповіщення підписників
  notifySubscribers(status) {
    this.subscribers.forEach(callback => callback(status));
  }

  // Отримання статусу з кешем
  async getStatus(forceRefresh = false) {
    const now = Date.now();
    
    // Повертаємо кеш якщо він свіжий і не потрібно примусове оновлення
    if (!forceRefresh && this.cache && (now - this.lastCheckTime < this.CACHE_DURATION)) {
      return this.cache;
    }

    // Уникаємо одночасні запити
    if (this.isChecking) {
      // Чекаємо поки поточний запит завершиться
      while (this.isChecking) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.cache;
    }

    return this.fetchStatus();
  }

  // Запит до сервера
  async fetchStatus() {
    try {
      this.isChecking = true;
      const token = localStorage.getItem('token');
      
      if (!token) {
        this.cache = null;
        this.notifySubscribers(null);
        return null;
      }

      const response = await axios.get(`${config.apiUrl}/api/auth/check-deletion-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      this.cache = response.data;
      this.lastCheckTime = Date.now();
      this.notifySubscribers(this.cache);
      
      return this.cache;
    } catch (error) {
      console.error('Error fetching deletion status:', error);
      this.cache = null;
      this.notifySubscribers(null);
      return null;
    } finally {
      this.isChecking = false;
    }
  }

  // Очищення кешу (наприклад, після зміни статусу)
  clearCache() {
    this.cache = null;
    this.lastCheckTime = 0;
  }

  // Перевірка чи користувач має запит на видалення
  hasDeletionRequest() {
    return this.cache?.deletionRequested || false;
  }

  // Запуск автоматичного опитування (тільки якщо є активні підписники)
  startPolling() {
    if (this.pollInterval || this.subscribers.size === 0) return;
    
    this.pollInterval = setInterval(() => {
      if (this.subscribers.size > 0) {
        this.getStatus(false); // Не форсуємо, використовуємо кеш
      } else {
        this.stopPolling(); // Зупиняємо якщо немає підписників
      }
    }, this.POLL_INTERVAL);
  }

  // Зупинка автоматичного опитування
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  // Оновлена підписка з автоматичним управлінням опитуванням
  subscribe(callback) {
    this.subscribers.add(callback);
    
    // Запускаємо опитування якщо це перший підписник
    if (this.subscribers.size === 1) {
      this.startPolling();
    }
    
    // Відразу повертаємо поточний статус якщо є
    if (this.cache) {
      callback(this.cache);
    }
    
    return () => {
      this.subscribers.delete(callback);
      // Зупиняємо опитування якщо немає підписників
      if (this.subscribers.size === 0) {
        this.stopPolling();
      }
    };
  }
}

// Експортуємо єдиний екземпляр
export const deletionStatusService = new DeletionStatusService();
export default deletionStatusService;